import { navigate } from "gatsby"
import React, { Suspense, useEffect, useRef, useState } from "react"
import {
  SITE_KEY,
  encodeToFormData,
  getUserCountry,
  isEmail,
  isUserInBlockedModalRegion,
  verifyEmailExists,
} from "../../utils"
import PhoneCustomInput from "./phoneInput"
import * as styles from "./AiEfficiencyModal.module.scss"
import "../arabic/ArabicPopupForms.scss"

const ReCAPTCHA = React.lazy(() => import("react-google-recaptcha"))

const STORAGE_KEY = "aiEfficiencyModalShown"
const DELAY_MS = 15_000
const COUNTDOWN_SECONDS = 6 * 60
const FORM_ID = 9091

const formatCountdown = total => {
  const safe = Math.max(0, total)
  const m = Math.floor(safe / 60)
  const s = safe % 60
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`
}

const AiEfficiencyModal = ({ language = "en" }) => {
  const isArabic = language === "ar"
  const copy = (english, arabic) => (isArabic ? arabic : english)
  const [regionBlocked, setRegionBlocked] = useState(false)
  const [regionResolved, setRegionResolved] = useState(false)
  const [open, setOpen] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [isDesktop, setIsDesktop] = useState(false)
  const [timeLeft, setTimeLeft] = useState(COUNTDOWN_SECONDS)
  const [status, setStatus] = useState("init")
  const [errors, setErrors] = useState({})
  const [recaptchaNeeded, setRecaptchaNeeded] = useState(false)
  const [mapBoxneeded, setBoxmapBoxneeded] = useState(false)
  const [defaultCountry, setDefaultCountry] = useState("")
  const [values, setValues] = useState({
    name: "",
    email: "",
    phoneContact: "",
    phoneNumber: "",
    countryCode: "",
    countryName: "",
    company: "",
    captcha: false,
  })
  const isSubmittingRef = useRef(false)
  useEffect(() => {
    if (typeof window === "undefined") return undefined

    let cancelled = false
    isUserInBlockedModalRegion()
      .then(blocked => {
        if (cancelled) return
        if (blocked) setRegionBlocked(true)
        setRegionResolved(true)
      })
      .catch(() => {
        if (!cancelled) setRegionResolved(true)
      })

    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    if (typeof window === "undefined" || !regionResolved || regionBlocked) {
      return undefined
    }

    const t = setTimeout(() => {
      setOpen(true)
    }, DELAY_MS)

    return () => clearTimeout(t)
  }, [regionResolved, regionBlocked])

  useEffect(() => {
    getUserCountry().then(setDefaultCountry)
  }, [])

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return
    const mq = window.matchMedia("(min-width: 901px)")
    const update = () => setIsDesktop(mq.matches)
    update()
    if (mq.addEventListener) {
      mq.addEventListener("change", update)
      return () => mq.removeEventListener("change", update)
    }
    mq.addListener(update)
    return () => mq.removeListener(update)
  }, [])

  useEffect(() => {
    if (!open) return
    const id = setInterval(() => {
      setTimeLeft(prev => (prev > 0 ? prev - 1 : 0))
    }, 1000)
    return () => clearInterval(id)
  }, [open])

  useEffect(() => {
    if (!open) return
    const onKey = e => {
      if (e.key === "Escape") setOpen(false)
    }
    document.addEventListener("keydown", onKey)

    const html = document.documentElement
    const body = document.body
    const scrollY = window.scrollY
    const scrollbarWidth = window.innerWidth - html.clientWidth

    const prev = {
      htmlOverflow: html.style.overflow,
      bodyOverflow: body.style.overflow,
      bodyPosition: body.style.position,
      bodyTop: body.style.top,
      bodyWidth: body.style.width,
      bodyPaddingRight: body.style.paddingRight,
    }

    html.style.overflow = "hidden"
    body.style.overflow = "hidden"
    body.style.position = "fixed"
    body.style.top = `-${scrollY}px`
    body.style.width = "100%"
    if (scrollbarWidth > 0) {
      body.style.paddingRight = `${scrollbarWidth}px`
    }

    return () => {
      document.removeEventListener("keydown", onKey)
      html.style.overflow = prev.htmlOverflow
      body.style.overflow = prev.bodyOverflow
      body.style.position = prev.bodyPosition
      body.style.top = prev.bodyTop
      body.style.width = prev.bodyWidth
      body.style.paddingRight = prev.bodyPaddingRight
      window.scrollTo(0, scrollY)
    }
  }, [open])

  if (regionBlocked || !open) return null

  const handleChange = e => {
    const { name, value } = e.target
    setValues(v => ({ ...v, [name]: value }))
    setRecaptchaNeeded(true)
    if (errors[name]) {
      const next = { ...errors }
      delete next[name]
      setErrors(next)
    }
  }

  const onChangeReCaptcha = value => {
    setValues(v => ({ ...v, captcha: value }))
    if (errors.captcha) {
      const next = { ...errors }
      delete next.captcha
      setErrors(next)
    }
  }

  const validate = data => {
    const newErrors = {}
    if (!data.name || !data.name.trim()) {
      newErrors.name = copy("This field is required!", "هذا الحقل مطلوب")
    }
    if (!data.email || !data.email.trim()) {
      newErrors.email = copy("This field is required!", "هذا الحقل مطلوب")
    } else if (!isEmail(data.email)) {
      newErrors.email = copy(
        "Email must be valid!",
        "يرجى إدخال بريد إلكتروني صحيح"
      )
    }
    if (!data.captcha) {
      newErrors.captcha = copy("This field is required!", "يرجى إكمال التحقق")
    }
    if (!data.phoneNumber) {
      newErrors.phoneNumber = copy("This field is required!", "هذا الحقل مطلوب")
    }

    return newErrors
  }

  const handleSubmit = async e => {
    e.preventDefault()
    if (isSubmittingRef.current) return
    setRecaptchaNeeded(true)

    const validationErrors = validate(values)
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors)
      setStatus("init")
      return
    }
    isSubmittingRef.current = true
    setStatus("submitting")
    await new Promise(resolve => setTimeout(resolve, 0))

    if (values.email && isEmail(values.email)) {
      try {
        const result = await verifyEmailExists(values.email)
        if (result.message) {
          setErrors({ email: result.message })
          setStatus("init")
          isSubmittingRef.current = false
          return
        }
      } catch (err) {
        console.warn("Email verification API error:", err)
      }
    }

    const url = `${process.env.GATSBY_STRAPI_API_URL}/api/contactusforms/?id=${FORM_ID}`

    const formData = encodeToFormData({
      fullName: values.name,
      email: values.email,
      phoneContact: values.phoneContact,
      countryCode: values.countryCode,
      countryName: values.countryName,
      company: values.company,
      serviceType: "AI Efficiency Audit",
      captchaToken: values.captcha,
    })

    try {
      const res = await fetch(url, {
        method: "POST",
        body: formData,
      })
      if (res.status === 429) {
        setStatus("failed")
        isSubmittingRef.current = false
        return
      }

      const result = await res.json()

      if (
        !result ||
        !result.hasOwnProperty("error") ||
        result.status === "mail_sent" ||
        result.status === "mail_failed"
      ) {
        setSubmitted(true)
        setOpen(false)
        return navigate("/thank-you/", {
          state: {
            pathname: window.location.pathname,
            fullName: values.name,
            email: values.email,
          },
        })
      } else {
        throw new Error("Server error!")
      }
    } catch (err) {
      setStatus("failed")
      isSubmittingRef.current = false
    }
  }

  const handleOverlayClick = e => {
    if (e.target === e.currentTarget) setOpen(false)
  }

  return (
    <div
      className={`${styles.overlay} ${
        isArabic ? "arabic-ai-efficiency-modal" : ""
      }`}
      lang={language}
      dir={isArabic ? "rtl" : "ltr"}
      onClick={handleOverlayClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="ai-efficiency-modal-title"
    >
      <div className={styles.modal}>
        {isDesktop && (
          <video
            className={styles.bgVideo}
            src="https://invozone-backend.s3.us-east-1.amazonaws.com/Turtle_Green_Background_1_63f438282c.webm"
            autoPlay
            loop
            muted
            playsInline
            preload="auto"
            aria-hidden="true"
          />
        )}

        <button
          type="button"
          aria-label={copy("Close", "إغلاق")}
          className={styles.closeBtn}
          onClick={() => setOpen(false)}
        >
          ×
        </button>

        <div className={styles.body}>
          <div className={styles.left}>
            <h2 id="ai-efficiency-modal-title" className={styles.heading}>
              {copy(
                "See How AI Can 3x Your Business Efficiency",
                "اكتشف كيف يمكن للذكاء الاصطناعي مضاعفة كفاءة أعمالك ٣ مرات"
              )}
            </h2>
            <p className={styles.subheading}>
              {copy(
                'We identify your "hidden" losses and provide a step by step plan to make your business 3x more efficient in just 30 days.',
                "نحدّد جوانب الهدر غير الظاهرة ونقدّم خطة خطوة بخطوة لرفع كفاءة أعمالك ثلاثة أضعاف خلال ٣٠ يومًا."
              )}
            </p>
          </div>

          <div className={styles.right}>
            {/* <div className={styles.countdown} aria-live="polite">
              <p className={styles.countdownLabel}>Offer expires in:</p>
              <p className={styles.countdownTime}>
                {formatCountdown(timeLeft)}
              </p>
            </div> */}

            {submitted ? (
              <div className={styles.success}>
                {copy(
                  "Thanks! We'll be in touch shortly.",
                  "شكرًا لك! سنتواصل معك قريبًا."
                )}
              </div>
            ) : (
              <div className={styles.formCard}>
                <form
                  className={styles.form}
                  onSubmit={handleSubmit}
                  noValidate
                >
                  <div className={styles.field}>
                    <label className={styles.label} htmlFor="ai-modal-name">
                      {copy("Name*", "الاسم*")}
                    </label>
                    <input
                      id="ai-modal-name"
                      name="name"
                      type="text"
                      placeholder={copy(
                        "Enter your full name",
                        "أدخل اسمك الكامل"
                      )}
                      value={values.name}
                      onChange={handleChange}
                      className={`${styles.input} ${
                        errors.name ? styles.inputError : ""
                      }`}
                    />
                    {errors.name && (
                      <small className={styles.errorText}>{errors.name}</small>
                    )}
                  </div>

                  <div className={styles.field}>
                    <label className={styles.label} htmlFor="ai-modal-email">
                      {copy("Email Address*", "البريد الإلكتروني*")}
                    </label>
                    <input
                      id="ai-modal-email"
                      name="email"
                      type="email"
                      placeholder={copy(
                        "Enter your email address.",
                        "أدخل بريدك الإلكتروني"
                      )}
                      value={values.email}
                      onChange={handleChange}
                      className={`${styles.input} ${
                        errors.email ? styles.inputError : ""
                      }`}
                    />
                    {errors.email && (
                      <small className={styles.errorText}>{errors.email}</small>
                    )}
                  </div>

                  <div className={styles.field}>
                    <div className={styles.label}>
                      {copy("Phone Number", "رقم الهاتف")}
                    </div>
                    <PhoneCustomInput
                      containerClass={`${styles.phoneWrap}${
                        errors.phoneNumber ? ` ${styles.phoneWrapHasError}` : ""
                      }`}
                      inputClass={`phoneInputAIModalInput ${styles.input} ${
                        errors.phoneNumber
                          ? `${styles.inputError} ${styles.phoneInputError} phoneInputError`
                          : ""
                      }`}
                      value={values.phoneContact}
                      defaultCountry={defaultCountry}
                      mapBoxneeded={mapBoxneeded}
                      onFocus={() => setBoxmapBoxneeded(true)}
                      onChange={(value, data) => {
                        const { dialCode, countryCode } = data
                        setValues(v => ({
                          ...v,
                          phoneContact: value,
                          phoneNumber: value?.slice(dialCode?.length),
                          countryCode: dialCode,
                          countryName: countryCode,
                        }))
                        setRecaptchaNeeded(true)
                        if (errors.phoneNumber) {
                          const national = (
                            value?.slice(dialCode?.length) || ""
                          ).replace(/\D/g, "")
                          if (
                            national.length >= 7 &&
                            national.length <= 15 &&
                            /^\d+$/.test(national)
                          ) {
                            const next = { ...errors }
                            delete next.phoneNumber
                            setErrors(next)
                          }
                        }
                      }}
                      isValid={(value, country) => {
                        if (!value || !value.trim()) return true
                        const dialCode = country?.dialCode || ""
                        const digits = (value || "").replace(/\D/g, "")
                        if (!digits.startsWith(dialCode))
                          return copy(
                            "Enter a valid phone number",
                            "يرجى إدخال رقم هاتف صحيح"
                          )
                        const national = (
                          value?.slice(dialCode.length) || ""
                        ).replace(/\D/g, "")
                        if (national.length === 0) return true
                        if (
                          national.length < 7 ||
                          national.length > 15 ||
                          !/^\d+$/.test(national)
                        ) {
                          return copy(
                            "Enter a valid phone number",
                            "يرجى إدخال رقم هاتف صحيح"
                          )
                        }
                        return true
                      }}
                    />
                    {errors.phoneNumber && (
                      <small className={styles.errorText}>
                        {errors.phoneNumber}
                      </small>
                    )}
                  </div>

                  <div className={styles.field}>
                    <label className={styles.label} htmlFor="ai-modal-company">
                      {copy(
                        "Company Name/Website",
                        "اسم الشركة / الموقع الإلكتروني"
                      )}
                    </label>
                    <input
                      id="ai-modal-company"
                      name="company"
                      type="text"
                      placeholder={copy(
                        "Company Name/Website",
                        "اسم الشركة / الموقع الإلكتروني"
                      )}
                      value={values.company}
                      onChange={handleChange}
                      className={styles.input}
                    />
                  </div>

                  {recaptchaNeeded && (
                    <div className={styles.captcha}>
                      <Suspense fallback={null}>
                        <ReCAPTCHA
                          sitekey={SITE_KEY}
                          hl={isArabic ? "ar" : "en"}
                          onChange={onChangeReCaptcha}
                          onExpired={() => {
                            setValues(v => ({ ...v, captcha: false }))
                            setErrors(prev => ({
                              ...prev,
                              captcha: copy(
                                "This field is required!",
                                "يرجى إكمال التحقق"
                              ),
                            }))
                          }}
                        />
                      </Suspense>
                      {errors.captcha && (
                        <small className={styles.errorText}>
                          {errors.captcha}
                        </small>
                      )}
                    </div>
                  )}

                  {status === "failed" && (
                    <div className={styles.errorText} role="alert">
                      {copy(
                        "Oops! Something went wrong while submitting the form.",
                        "حدث خطأ أثناء إرسال النموذج. يرجى المحاولة مرة أخرى."
                      )}
                    </div>
                  )}

                  <button
                    type="submit"
                    className={styles.submit}
                    disabled={status === "submitting"}
                  >
                    {status === "submitting" ? (
                      <span className={styles.spinner} aria-hidden="true" />
                    ) : (
                      copy(
                        "Claim My Free AI-Audit",
                        "احصل على تقييم مجاني للذكاء الاصطناعي"
                      )
                    )}
                  </button>

                  <p className={styles.notice}>
                    <span aria-hidden="true">⚠️</span>
                    {copy(
                      "Only 2 priority spots left this month",
                      "تبقّى مكانان فقط هذا الشهر"
                    )}
                  </p>
                </form>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default AiEfficiencyModal
